import { useId, useState, type CSSProperties, type ReactNode } from 'react'
import css from './ui.module.css'
import { TRACK, type Stage } from '../data/map/stage'

export function Card({ children, dashed, style }: { children: ReactNode; dashed?: boolean; style?: CSSProperties }) {
  return <div className={dashed ? `${css.card} ${css.cardDashed}` : css.card} style={style}>{children}</div>
}

export function Chip({ children, neutral }: { children: ReactNode; neutral?: boolean }) {
  return <span className={neutral ? `${css.chip} ${css.chipNeutral}` : css.chip}>{children}</span>
}

// Unrendered on purpose: SPEC.md line 78 draws it as the state pill on the
// Linear block for dev queue items, and that block is unbuilt because the
// Linear state is not in `approvals.context` yet. An unfinished feature, not
// cruft -- deleting it means re-deriving it from the same spec line.
export function Pill({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span className={css.pill} style={style}>{children}</span>
}

type ButtonProps = {
  children: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'quiet'
  disabled?: boolean
  title?: string
  type?: 'button' | 'submit'
  style?: CSSProperties
}

export function Button({ children, onClick, variant = 'default', disabled, title, type = 'button', style }: ButtonProps) {
  const cls =
    variant === 'primary' ? `${css.button} ${css.buttonPrimary}`
    : variant === 'quiet' ? `${css.button} ${css.buttonQuiet}`
    : css.button
  return (
    <button className={cls} onClick={onClick} disabled={disabled} title={title} type={type} style={style}>
      {children}
    </button>
  )
}

/**
 * `className` is for the layout class of the cell the label sits in -- a grid
 * column's alignment, not a second opinion about how a micro label looks. It
 * exists so a head cell that needs both can still be the component rather than
 * the bare class beside its own layout class.
 */
export function MicroLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={className ? `microLabel ${className}` : 'microLabel'}>{children}</div>
}

export function Mono({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span className="mono" style={style}>{children}</span>
}

export function ViewHeader({ title, aside }: { title: string; aside?: ReactNode }) {
  return (
    <div className={css.viewHeader}>
      <h2 className={css.viewTitle}>{title}</h2>
      {aside ? <div className={css.viewAside}>{aside}</div> : null}
    </div>
  )
}

export function StageTrack({ stage }: { stage: Stage }) {
  const ink = `var(--s-${stage.token})`
  const fillPct = (stage.position / (TRACK.length - 1)) * 100
  return (
    <div className={css.track}>
      <div className={css.rail} />
      <div className={css.railFill} style={{ width: `${fillPct}%`, background: ink }} />
      {TRACK.map((label, i) => {
        const pct = (i / (TRACK.length - 1)) * 100
        const isCurrent = i === stage.position
        const passed = i < stage.position
        const tickCls =
          i === 0 ? `${css.tick} ${css.tickFirst}`
          : i === TRACK.length - 1 ? `${css.tick} ${css.tickLast}`
          : css.tick
        const dotCls = [
          css.dot,
          isCurrent ? css.dotCurrent : css.dotOther,
          isCurrent && stage.pulse ? css.dotPulse : '',
        ].filter(Boolean).join(' ')
        return (
          <div key={label} className={tickCls} style={{ left: `${pct}%` }}>
            <div
              className={dotCls}
              style={{ background: isCurrent ? ink : passed ? 'var(--ink-3)' : 'var(--line)' }}
            />
            <div
              className={isCurrent ? `${css.tickLabel} ${css.tickLabelCurrent}` : css.tickLabel}
              style={{ color: isCurrent ? 'var(--ink)' : 'var(--ink-3)' }}
            >
              {label}
            </div>
          </div>
        )
      })}
      {stage.note ? (
        <div
          className={css.trackNote}
          style={
            stage.position === 0 ? { left: 0 }
            : stage.position === TRACK.length - 1 ? { right: 0 }
            : { left: `${fillPct}%`, transform: 'translateX(-50%)' }
          }
        >
          {stage.note}
        </div>
      ) : null}
    </div>
  )
}

export function StatCard({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <Card style={{ padding: '13px 16px' }}>
      <MicroLabel>{label}</MicroLabel>
      <div className={`${css.statValue} mono`} style={{ marginTop: 5 }}>{value}</div>
      <div className={css.statNote}>{note}</div>
    </Card>
  )
}

export function Empty({ glyph, children }: { glyph: string; children: ReactNode }) {
  return (
    <div className={css.empty}>
      <div className={`${css.emptyGlyph} mono`}>{glyph}</div>
      <p className={css.emptyText}>{children}</p>
    </div>
  )
}

/**
 * A view with no data source yet says so plainly and names what unblocks it.
 * Inventing rows to fill the layout would put a fiction in front of the two
 * people who are meant to trust this screen.
 */
export function Blocked({ title, children, meta }: { title: string; children: ReactNode; meta?: ReactNode }) {
  return (
    <Card>
      <div className={css.blocked}>
        <h3 className={css.blockedTitle}>{title}</h3>
        <div className={css.blockedBody}>{children}</div>
        {meta ? <div className={css.blockedMeta}>{meta}</div> : null}
      </div>
    </Card>
  )
}

export function Notice({ children }: { children: ReactNode }) {
  return <div className={css.notice}>{children}</div>
}

export function QueryError({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error)
  return (
    <Card>
      <div className={css.errorBox}>
        Could not read from the fleet database. The view is showing nothing rather than
        stale numbers.
        <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 7 }}>{message}</div>
      </div>
    </Card>
  )
}

/**
 * A section that opens to its contents and, closed, says what they are.
 *
 * Collapsed is the default wherever a page holds several of something -- lanes,
 * databases, directory entries -- so the page reads as a list of summaries and a
 * person opens the one they came for. The summary is the point: a closed section
 * that does not say what is inside is a thing someone has to open to find out
 * whether it mattered, which is the long page this replaces.
 *
 * `aside` sits beside the toggle rather than inside it, for controls that must
 * stay reachable while it is closed (a desk's pillar filters); nothing
 * interactive goes inside a button. Pass `open` to control it from outside --
 * a pillar pick or a runbook jump opens the section it lands in -- otherwise it
 * keeps its own state, which a poll re-render does not reset.
 *
 * No motion: the caret turns, it does not animate (tokens.css allows one
 * animation in the product and this is not it). 44px tall for rule 5.
 */
export function Disclosure({
  title, summary, aside, open, defaultOpen = false, onOpenChange, children, flush, label, id,
}: {
  /** Plain text when `label` is set: it renders as the micro label, the way a card's head does. */
  title: ReactNode
  /** One line of what is inside. A caller whose summary would repeat the body passes none while open. */
  summary?: ReactNode
  aside?: ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode
  /** A section on the page rather than in a card: no padding of its own and no rule under the head. */
  flush?: boolean
  /**
   * A card's head or a list's sub-head rather than a named thing: the title is a
   * micro label and the summary runs beside it, as the heads it replaces did.
   */
  label?: boolean
  id?: string
}) {
  const [own, setOwn] = useState(defaultOpen)
  const isOpen = open ?? own
  const bodyId = useId()
  const toggle = () => {
    const next = !isOpen
    if (open === undefined) setOwn(next)
    onOpenChange?.(next)
  }
  const head = [
    css.disclosureHead,
    flush ? css.disclosureFlush : '',
    label ? css.disclosureLabel : '',
    isOpen ? css.disclosureHeadOpen : '',
  ].filter(Boolean).join(' ')
  return (
    <div className={css.disclosure} id={id}>
      <div className={head}>
        <button
          type="button"
          className={css.disclosureToggle}
          aria-expanded={isOpen}
          // Only while there is a body to point at; closed, it is not rendered.
          aria-controls={isOpen ? bodyId : undefined}
          onClick={toggle}
        >
          <span className={isOpen ? `${css.caret} ${css.caretOpen}` : css.caret} aria-hidden />
          <span className={css.disclosureText}>
            <span className={label ? 'microLabel' : css.disclosureTitle}>{title}</span>
            {summary ? <span className={css.disclosureSummary}>{summary}</span> : null}
          </span>
        </button>
        {aside ? <div className={css.disclosureAside}>{aside}</div> : null}
      </div>
      {isOpen ? <div id={bodyId} className={flush ? undefined : css.disclosureBody}>{children}</div> : null}
    </div>
  )
}
